import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, switchMap, first, tap} from 'rxjs/operators';
import { of } from 'rxjs';
import * as ProductsActions from '../actions/products.actions';
import { ProductsService } from '@app/services/products/product.service';
import { Store } from '@ngrx/store';
import { CategoriesService } from '@app/services/categories/categories.service';
import { AreasService } from '@app/services/areas/areas.service';
import { ProductBarcodesService } from '@app/services/product-barcodes/product-barcodes.service';
import { selectProductsDirectionState, selectProductsSortState } from '../selectors/products.selectors';
import { ToastrService } from 'ngx-toastr';
import * as CoreActions from '@app/core/store/core.action';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { Location } from '@angular/common';

@Injectable()
export class ProductsEffects {

  loadProducts$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ProductsActions.loadProducts),
      switchMap(action => {
        // Fall back to the filters held in the store for anything this action
        // did not carry. By the time an effect runs, the reducer has already
        // folded the action in, so the store holds the effective filter set.
        // Callers that only pass a query - the Excel import refresh, product
        // add, several dialogs - used to send `undefined` as the storage
        // scope, which the backend reads as "no location filter" and answers
        // with the entire franchise catalog. The count then jumped until the
        // page was reloaded.
        let prdStorage = action.prdStorage;
        let prdCatID = action.prdCatID;
        let prdIsAsset = action.prdIsAsset;
        this.store$.select(state => (state as any).products).pipe(first()).subscribe(s => {
          if (prdStorage === undefined) { prdStorage = s?.prdStorage; }
          if (prdCatID === undefined) { prdCatID = s?.prdCatID; }
          if (prdIsAsset === undefined) { prdIsAsset = s?.prdIsAsset; }
        });
        return this._products.getProducts('prdNameEn', 'asc', action.query, '', prdStorage, prdCatID, prdIsAsset).pipe(
          map((data): any => {
            if(data.result!=null && data.result!='undefined' && data.result=='failed.access' && this.location.path()=='/products'){
              this.store$.dispatch(CoreActions.showPermissionMessage({
                title: this._translation.instant('errormessage.warning'),
                body: this._translation.instant('errormessage.access')
              }));
            }

            return ProductsActions.loadProductsSuccess({ products: data.products, total: data.total })
          }),
          catchError(error => {
            return of(ProductsActions.loadProductsFailure({ error }))
          })
        )
      })
    );
  });

  loadProductsByCategory$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ProductsActions.filterByCategory),
      switchMap(action => {
        return this._products.getProductsByCategory(action.prdCatID, action.active, action.direction).pipe(
          map((data): any => {
            return ProductsActions.filterByCategorySuccess({ products: data.products, total: data.total })
          }),
          catchError(error => {
            return of(ProductsActions.filterByCategoryFailure({ error }))
          })
        )
      })
    );
  });

  loadProductsToggleStorage$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ProductsActions.filterToggleStorage),
      switchMap(action => {
        return this._products.getProductsToggleStorage(action.prdStorage, action.active, action.direction).pipe(
          map((data): any => {
            return ProductsActions.filterToggleStorageSuccess({ products: data.products, total: data.total })
          }),
          catchError(error => {
            return of(ProductsActions.filterToggleStorageFailure({ error }))
          })
        )
      })
    );
  });

  loadProductsByArea$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ProductsActions.filterByArea),
      switchMap(action => {
        return this._products.getProductsByArea(action.prdAreaID, action.active, action.direction).pipe(
          map((data): any => {
            return ProductsActions.filterByAreaSuccess({ products: data.products, total: data.total })
          }),
          catchError(error => {
            return of(ProductsActions.filterByAreaFailure({ error }))
          })
        )
      })
    );
  });

  loadProductsByStorages$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ProductsActions.filterByStorages),
      switchMap(action => {
        return this._products.getProductsByStorages(action.prdStoragesIDs, action.active, action.direction).pipe(
          map((data): any => {
            return ProductsActions.filterByStoragesSuccess({ products: data.products, total: data.total })
          }),
          catchError(error => {
            return of(ProductsActions.filterByStoragesFailure({ error }))
          })
        )
      })
    );
  });

  addProductsToStorages$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ProductsActions.addProductStorages),
      switchMap(action => {
        return this._products.addProductsByStorages(action.productIDs, action.storageIDs).pipe(
          map((data): any => {
            this.store$.dispatch(ProductsActions.setDeselected());
            // Reload the list with the active filter so products placed into
            // storage drop off the "Not in Storage" view immediately.
            this.store$.dispatch(ProductsActions.loadProducts({
              query: action.query || '',
              prdStorage: action.prdStorage,
              prdCatID: action.prdCatID
            }));
            return ProductsActions.addProductStoragesSuccess({ product: data })
          }),
          catchError(error => {
            return of(ProductsActions.addProductStoragesFailure({ error }))
          })
        )
      })
    );
  });

  searchProducts$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ProductsActions.search),
      switchMap(action => {
        let direction: string;
        let sort: string;
        // this.store$.select(selectProductsDirectionState).pipe(first()).subscribe(res => direction = res);
        // this.store$.select(selectProductsSortState).pipe(first()).subscribe(res => sort = res);

        return this._products.searchProducts('prdNameEn', 'ASC', action.query).pipe(
          map((data): any => {
            return ProductsActions.searchSuccess({ products: data.products, total: data.total })
          }),
          catchError(error => {
            return of(ProductsActions.searchFailure({ error }))
          })
        )
      })
    );
  });

  createProduct$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ProductsActions.saveProduct),
      switchMap(action => {
        return this._products.onSave(action.form).pipe(
          map((response): any => {
            if(response.result!='undefined' && response.result=='failed.max'){
              this.store$.dispatch(CoreActions.showErrorMessage({
                title: this._translation.instant('errormessage.warning'),
                body: this._translation.instant('errormessage.limitproduct'),
                show_upgrade: true
              }));
              return of(ProductsActions.saveProductFailure({ error: 'An error occurred.' }))
            }

            if(response.error && response.message!=''){
              //return ProductsActions.saveProductSuccess(response)
              this._toastr.error(this._translation.instant(response.message, response.params || {}));
              return ProductsActions.saveProductFailure({ error: 'An error occurred.' })
            }
          
              const { product } = response;
              if (product !== undefined) {
                this.store$.dispatch(CoreActions.increaseCurrentLimit({ types: 'products' }));
                return ProductsActions.saveProductSuccess(response)
              } else {
                return of(ProductsActions.saveProductFailure({ error: 'An error occurred.' }))
              }
            
          }),
          catchError(error => {
            return of(ProductsActions.saveProductFailure({ error }))
          })
        )
      })
    );
  });

  updateProduct$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ProductsActions.updateProduct),
      switchMap(action => {
        return this._products.updateProduct(action.form).pipe(
          map((response): any => {
            if(response.error && response.message!=''){
              this._toastr.error(this._translation.instant(response.message, response.params || {}));
              return ProductsActions.updateProductFailure({ error: 'An error occurred.' })
            }

            return ProductsActions.updateProductSuccess(response)
          }),
          catchError(error => {
            if (error?.status === 422 && error?.error?.message) {
              this._toastr.error(this._translation.instant(error.error.message));
            }
            return of(ProductsActions.updateProductFailure({ error }))
          })
        )
      })
    );
  });

  loadProduct$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ProductsActions.loadProduct),
      switchMap(action => {
        return this._products.getProduct(action.prdID).pipe(
          map(({ product }): any => {
            return ProductsActions.loadProductSuccess({ product })
          }),
          catchError(error => {
            return of(ProductsActions.loadProductFailure({ error }))
          })
        )
      })
    );
  });

  deleteProducts$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ProductsActions.deleteProducts),
      switchMap(action => {
        return this._products.batchDelete(action.selected, action.reason).pipe(
          map(() => {
            this.store$.dispatch(CoreActions.decreaseCurrentLimit({ types: 'products', decrease_count :action.selected.length }));
            return ProductsActions.deleteProductsSuccess({ query: action.query,prdStorage:action.prdStorage});}),
          catchError(error => {
            return of(ProductsActions.deleteProductsFailure({ error }))
          })
          )
      })
    );
  });

  removeProductsFromLocation$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ProductsActions.removeProductsFromLocation),
      switchMap(action => {
        return this._products.bulkRemoveFromLocation(action.selected, action.reason).pipe(
          map(() => ProductsActions.removeProductsFromLocationSuccess({
            query: action.query,
            prdStorage: action.prdStorage,
            prdCatID: action.prdCatID
          })),
          catchError(error => of(ProductsActions.removeProductsFromLocationFailure({ error })))
        );
      })
    );
  });

  refreshProductsAfterDelete$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ProductsActions.deleteProductsSuccess, ProductsActions.removeProductsFromLocationSuccess),
      switchMap(action => {
        let filterType: string;
        let filterID: any;
        let sortType: string;
        let direction: string;

        this.store$.select(state => (state as any).products).pipe(first()).subscribe(productsState => {
          filterType = productsState.filterType;
          filterID = productsState.filterID;
          sortType = productsState.sort;
          direction = productsState.direction;
        });

        return of(0).pipe(
          map(() => {
            switch(filterType) {
              case 'category':
                return ProductsActions.filterByCategory({
                  prdCatID: filterID,
                  active: sortType || 'prdCatPositionning',
                  direction: direction || 'ASC'
                });
              case 'toggleStorage':
                return ProductsActions.filterToggleStorage({
                  prdStorage: filterID,
                  active: sortType || '',
                  direction: direction || 'ASC'
                });
              case 'storages':
                return ProductsActions.filterByStorages({
                  prdStoragesIDs: filterID,
                  active: sortType || 'prdNameEn',
                  direction: direction || 'ASC'
                });
              default:
                return ProductsActions.loadProducts({query: action.query, prdStorage: action.prdStorage, prdCatID: (action as any).prdCatID});
            }
          }),
          catchError(error => of(ProductsActions.deleteProductsFailure({ error }))
          ))
      })
    );
  });

  refreshProductAfterDelete$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ProductsActions.deleteProductSuccess),
      switchMap(action => {
        let filterType: string;
        let filterID: any;
        let sortType: string;
        let direction: string;

        this.store$.select(state => (state as any).products).pipe(first()).subscribe(productsState => {
          filterType = productsState.filterType;
          filterID = productsState.filterID;
          sortType = productsState.sort;
          direction = productsState.direction;
        });

        return of(0).pipe(
          map(() => {
            switch(filterType) {
              case 'category':
                return ProductsActions.filterByCategory({
                  prdCatID: filterID,
                  active: sortType || 'prdCatPositionning',
                  direction: direction || 'ASC'
                });
              case 'toggleStorage':
                return ProductsActions.filterToggleStorage({
                  prdStorage: filterID,
                  active: sortType || '',
                  direction: direction || 'ASC'
                });
              case 'storages':
                return ProductsActions.filterByStorages({
                  prdStoragesIDs: filterID,
                  active: sortType || 'prdNameEn',
                  direction: direction || 'ASC'
                });
              default:
                return ProductsActions.loadProducts({query: action.query, prdStorage: action.prdStorage});
            }
          }),
          catchError(error => of(ProductsActions.deleteProductFailure({ error }))
          ))
      })
    );
  });

  cleanProductsAfterDelete$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ProductsActions.deleteProductsSuccess, ProductsActions.removeProductsFromLocationSuccess),
      switchMap(action => {
        return of(0).pipe(
          map(() => ProductsActions.setSelected({ selected: [] })),
          catchError(error => of(ProductsActions.deleteProductsFailure({ error }))
          ))
      })
    );
  });

  deleteProduct$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ProductsActions.deleteProduct),
      switchMap(action => {
        return this._products.onDelete(action.prdID).pipe(
          tap(() => {
            // this._toastr.error(`Successfully deleted product.`);
          }),
          map((): any => {
            this.store$.dispatch(CoreActions.decreaseCurrentLimit({ types: 'products' }));
            return ProductsActions.deleteProductSuccess({ prdID: action.prdID, query: action.query,prdStorage:action.prdStorage})
          }),
          catchError(error => {
            return of(ProductsActions.deleteProductFailure({ error: error.message }))
          })
        )
      })
    );
  });


  setSort$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ProductsActions.setSort),
      switchMap((action: any) => {
        // Read the active combined filters from store so a column sort
        // doesn't drop search/category/in-storage selections.
        let prdStorage: any = null;
        let prdCatID: any = 0;
        this.store$.select(state => (state as any).products).pipe(first()).subscribe(s => {
          prdStorage = s?.prdStorage;
          prdCatID = s?.prdCatID;
        });
        return this._products.getProducts(action.sort, action.direction, action.query, '', prdStorage, prdCatID).pipe(
          map((data): any => {
            return ProductsActions.loadProductsSuccess({ products: data.products, total: data.total })
          })
        )
      })
    );
  });

  constructor(
    private actions$: Actions, private _products: ProductsService,
    private store$: Store, private _categories: CategoriesService,
    private _areas: AreasService,
    private _barcode: ProductBarcodesService,
    private _toastr: ToastrService,
    public _router: Router,
    private location : Location,
    public _translation: TranslateService
  ) { }

}
